const TOKEN_KEY = 'roe_auth_token';
const USER_KEY = 'roe_user';

/** Per-tab auth storage. Using sessionStorage instead of localStorage so that logging into two
 *  different accounts (e.g. admin + normal) in two tabs / windows of the same browser stays
 *  isolated — each tab keeps its own token. localStorage is shared across all tabs of an origin,
 *  which caused the last-logged-in account to stomp every other tab's auth.
 *  Trade-off: closing the tab ends the session; reopening requires re-login. */
function authStore(): Storage {
  return typeof window !== 'undefined' ? window.sessionStorage : ({
    getItem: () => null, setItem: () => {}, removeItem: () => {}, key: () => null, length: 0, clear: () => {},
  } as unknown as Storage);
}

/** One-time migration: move tokens from localStorage to sessionStorage. */
function migrateLegacyLocalStorageAuth(): void {
  if (typeof window === 'undefined') return;
  const ls = window.localStorage;
  const ss = window.sessionStorage;
  const legacyToken = ls.getItem(TOKEN_KEY);
  const legacyUser = ls.getItem(USER_KEY);
  if (legacyToken || legacyUser) {
    if (legacyToken && !ss.getItem(TOKEN_KEY)) ss.setItem(TOKEN_KEY, legacyToken);
    if (legacyUser && !ss.getItem(USER_KEY)) ss.setItem(USER_KEY, legacyUser);
    ls.removeItem(TOKEN_KEY);
    ls.removeItem(USER_KEY);
  }
}

export interface AuthUser {
  id: string;
  username?: string | null;
  email?: string | null;
  walletAddress?: string | null;
  role?: string | null;
}

export function isProduction(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname !== 'localhost' ||
    (import.meta as any).env?.MODE === 'production'
  );
}

type LoginCallback = (user: AuthUser) => void;
type LogoutCallback = () => void;

function getApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

export class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private user: AuthUser | null = null;
  private onLoginCallbacks: LoginCallback[] = [];
  private onLogoutCallbacks: LogoutCallback[] = [];

  /** Called after successful login/wallet-login — wired to SyncService.fetchAndMerge in MenuScene */
  static onLoginSync: (() => Promise<void>) | null = null;
  /** Called after successful register — wired to SyncService.importFromLocalStorage in MenuScene */
  static onRegisterSync: (() => Promise<void>) | null = null;

  private constructor() {
    migrateLegacyLocalStorageAuth();
    const store = authStore();
    this.token = store.getItem(TOKEN_KEY);
    const stored = store.getItem(USER_KEY);
    this.user = stored ? (JSON.parse(stored) as AuthUser) : null;
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Reset for testing purposes
  static _resetInstance(): void {
    (AuthService as any).instance = undefined;
  }

  onLogin(cb: LoginCallback) { this.onLoginCallbacks.push(cb); }
  onLogout(cb: LogoutCallback) { this.onLogoutCallbacks.push(cb); }

  private persist(token: string, user: AuthUser) {
    this.token = token;
    this.user = user;
    const store = authStore();
    store.setItem(TOKEN_KEY, token);
    store.setItem(USER_KEY, JSON.stringify(user));
    this.onLoginCallbacks.forEach(cb => cb(user));
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${getApiBase()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json() as { token?: string; user?: AuthUser; error?: string } & T;
    if (!res.ok) throw new Error((data as any).error ?? 'Request failed');
    return data;
  }

  async register(email: string, password: string, username?: string): Promise<AuthUser> {
    const { token, user } = await this.post<{ token: string; user: AuthUser }>(
      '/api/auth/register',
      { email, password, username: username || undefined }
    );
    this.persist(token, user);
    AuthService.onRegisterSync?.().catch(() => { /* offline — ignore */ });
    return user;
  }

  async login(email: string, password: string): Promise<AuthUser> {
    const { token, user } = await this.post<{ token: string; user: AuthUser }>(
      '/api/auth/login',
      { email, password }
    );
    this.persist(token, user);
    AuthService.onLoginSync?.().catch(() => { /* offline — ignore */ });
    return user;
  }

  /** Request a password-reset email. Always resolves (server returns 200 even
   *  for unknown emails — no user-enumeration). */
  async forgotPassword(email: string): Promise<void> {
    await this.post<{ ok: true }>('/api/auth/forgot-password', { email });
  }

  /** Submit the new password with the token from the reset link. Throws on
   *  server 400 (invalid / expired / used token, or password too short)
   *  with the server's error message for inline display. */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.post<{ ok: true }>('/api/auth/reset-password', { token, newPassword });
  }

  logout(): void {
    this.token = null;
    this.user = null;
    const store = authStore();
    store.removeItem(TOKEN_KEY);
    store.removeItem(USER_KEY);
    this.onLogoutCallbacks.forEach(cb => cb());
  }

  getToken(): string | null { return this.token; }
  getUser(): AuthUser | null { return this.user; }
  isLoggedIn(): boolean { return this.token !== null; }
  isApproved(): boolean { return this.user?.role === 'approved' || this.user?.role === 'admin'; }
  isAdmin(): boolean { return this.user?.role === 'admin'; }

  /** Update username on the server and persist locally. */
  async updateUsername(username: string): Promise<void> {
    if (!this.token) throw new Error('Not logged in');
    const res = await fetch(`${getApiBase()}/api/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify({ username }),
    });
    const data = await res.json() as { user?: AuthUser; error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Update failed');
    if (data.user) {
      this.user = data.user;
      authStore().setItem(USER_KEY, JSON.stringify(data.user));
    }
  }

  /** Fetch fresh user data from /api/auth/me and update stored role.
   *  If the server returns 401 (token invalidated/rotated), clears the session and reloads. */
  async refreshFromServer(): Promise<void> {
    if (!this.token) return;
    try {
      const res = await fetch(`${getApiBase()}/api/auth/me`, { headers: this.authHeaders() });
      if (res.status === 401) {
        this.logout();
        if (typeof window !== 'undefined') window.location.reload();
        return;
      }
      if (res.ok) {
        const { user } = await res.json() as { user: AuthUser };
        this.user = user;
        authStore().setItem(USER_KEY, JSON.stringify(user));
      }
    } catch {
      // Network error — keep existing stored data
    }
  }

  authHeaders(): Record<string, string> {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }
}
